/**
 * @file Map-marker-coord-form.tsx
 * @module MapMarkerCoordForm
 * @description Koordinātu ievades formas sadaļa marķieru komponentam (jaunā versija).
 *              Atbalsta koordinātu ievadi EPSG:4326 (WGS84) un EPSG:3059 (LKS-92) formātos.
 *              Automātiski nosaka koordinātu sistēmu pēc vērtību diapazona un veic
 *              transformāciju uz kartes projekciju (EPSG:3857).
 */
import { QuestionCircleOutlined } from "@ant-design/icons";
import { Input, Switch, Tooltip } from "antd";
import { useContext, useEffect, useState } from "react";
import { findError, NewFeatureListContext } from "../Map-marker-list-wrapper";
import { MultiLineString, Point, SimpleGeometry } from "ol/geom";
import { fromLonLat, toLonLat, transform } from "ol/proj";
import { containsXY } from "ol/extent";
import { MINIMAP } from "../Map-marker-mini-map";
import { Feature } from "ol";

const getCoords = (feature: Feature) => {
	const geometry = feature!.getGeometry();

	if (geometry instanceof MultiLineString) {
		return geometry
			.getCoordinates()
			.map((line) =>
				line.map((coord) => {
					const [lon, lat] = toLonLat(coord);
					return `${+lat.toFixed(6)}, ${+lon.toFixed(6)}`;
				})
			)
			.join(", ");
	}
	if (geometry instanceof Point) {
		const [lon, lat] = toLonLat(geometry.getCoordinates());
		if (lon === 0 || lat === 0) return "";
		return `${+lat.toFixed(6)}, ${+lon.toFixed(6)}`;
	}
	return "";
};

export const MapMarkerCoordForm = () => {
	const { selectedItem, sticky, errors, readOnly } = useContext(NewFeatureListContext);
	const [coords, setCoords] = useState(getCoords(selectedItem!));

	const error = findError(selectedItem, errors);

	useEffect(() => {
		const updateCoords = () => {
			setCoords(getCoords(selectedItem!));
		};
		selectedItem!.on("change", updateCoords);
		return () => {
			selectedItem!.un("change", updateCoords);
		};
	}, [selectedItem]);

	const updateCoords = (e: any) => {
		const geom = selectedItem!.getGeometry();
		let [x, y] = e.target.value.split(",");

		if (!x) x = 0;
		if (!y) y = 0;

		const proj = containsXY([189423.14, 169354.52, 766599.76, 449393.56], x, y) ? ["EPSG:3059", "EPSG:3857"] : ["EPSG:4326", "EPSG:3857"];
		if (proj[0] === "EPSG:4326") {
			const latLon = fromLonLat([y, x], "EPSG:3059");
			if (containsXY([189423.14, 169354.52, 766599.76, 449393.56], latLon[0], latLon[1])) {
				const a = x;
				const b = y;
				x = b;
				y = a;
			}
		}

		if (geom instanceof Point) {
			const numbX = Number(x ?? 0);
			const numbY = Number(y ?? 0);

			geom.setCoordinates(transform([numbX > 0 ? numbX : 0, numbY > 0 ? numbY : 0], proj[0], proj[1]).map((v) => (isNaN(v) ? 0 : v)));
		}

		if (geom instanceof MultiLineString) {
			const values = e.target.value.split(",");
			const coords = [];

			for (let i = 0; i < values.length; i = i + 2) {
				const x = values[i];
				const y = values[i + 1];
				coords.push(transform([Number(x), Number(y)], proj[0], proj[1]));
			}

			geom.setCoordinates([coords]);
		}
		selectedItem!.setProperties({
			transformedCoords: e.target.value,
			proj: proj,
			trigger: !selectedItem?.get("trigger"),
			DONT_UPDATE: false,
			updatedlr: false
		});

		if (geom) MINIMAP.getView()?.fit(geom as SimpleGeometry, { padding: [16, 16, 16, 16], maxZoom: 12, duration: 500 });
	};

	const updateFeatureSticky = (value: boolean) => {
		selectedItem!.setProperties({ sticky: value, trigger: !selectedItem?.get("trigger"), DONT_UPDATE: false, updatedlr: false, start: '', end: '', street: '' });
	};

	return (
		<div className="grid grid-cols-4 items-center text-right gap-2 mt-4">
			<p>
				Koordinātas{" "}
				<Tooltip title="Ievadiet koordinātas WGS84 koordinātu sistēmā, formātā (lat) (lon).">
					<QuestionCircleOutlined className="text-gray-500" rev={undefined} />
				</Tooltip>{" "}
				:
			</p>
			<Input
				disabled={readOnly}
				value={coords}
				onChange={(e) => {
					const val = e.target.value;
					setCoords(val);
					if (/^\s*-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?\s*$/.test(val)) {
						const [x, y] = val.split(",").map((v) => Number(v));
						if (
							containsXY([20.9686, 55.6749, 28.2411, 58.0856], x, y) ||
							containsXY([20.9686, 55.6749, 28.2411, 58.0856], y, x) ||
							containsXY([189423.14, 169354.52, 766599.76, 449393.56], x, y) ||
							containsXY([189423.14, 169354.52, 766599.76, 449393.56], y, x)
						) {
							updateCoords(e);
						}
					}
				}}
				className="col-span-3"
				placeholder={"Piemērs: 56.9677, 24.1056"}
				id={"coord"}
				status={error?.error === "point_error" || error?.error === "general_error" ? "error" : ""}
			/>
			{(error?.error === "point_error" || error?.error === "general_error") && (
				<p className="col-start-2 col-span-3 text-[#ff4d4f] text-center leading-tight">{error.message}</p>
			)}
			<p>
				Piesaistīt tuvākajam ceļam{" "}
				<Tooltip title="Vieta tiks piesaistīta pret tuvākā valsts autoceļa brauktuvi.">
					<QuestionCircleOutlined className="text-gray-500" rev={undefined} />
				</Tooltip>{" "}
				:
			</p>
			<div className="col-span-3 flex items-start">
				<Switch disabled={readOnly} className="col-span-3 bg-[#bfbfbf]" id={"switcher"} defaultChecked={selectedItem!.get("sticky") ?? sticky} onChange={(v) => updateFeatureSticky(v)} />
			</div>
		</div>
	);
};
